import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'auth/Auth';
import { previewPrivateObject, uploadPendingTripImage } from 'utils/S3';
import {
    CONTINENTS,
    filterGeographyCountriesForContinent,
    isCountryInContinentList,
    normalizeContinentLabel,
} from 'data/continentCountries';
import {
    COUNTRY_ALREADY_EXISTS_MESSAGE,
    CLONE_COUNTRY_REQUIRED_MESSAGE,
    collectTakenCountries,
    isCountryTaken,
} from './wizard/utils/destinationCountry';
import { useCurrencyCodes } from 'hooks/useCurrencyCodes';
import { useLanguages } from 'hooks/useLanguages';
import { useCountries } from 'hooks/useCountries';
import {
    tripHeroImageHint,
    validateTripHeroImage,
} from './utils/tripImageDimensions';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Alert, Snackbar } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import StatusBadge from 'components/StatusBadge';
import MonoText from 'components/MonoText';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import AddHighlightDialog from './stepForm/components/AddHighlightDialog';
import HtmlFieldWithLimit from './stepForm/components/HtmlFieldWithLimit';
import { serializeHighlightsForApi } from './wizard/utils/formHelpers';
import { getQuillPlainLength } from 'components/RichTextEditor';
import { matchCountryValue } from './stepForm/utils/formHelpers';
import { outlinedFieldProps } from './stepForm/utils/fieldStyles';
import {
    TravelDetailFieldGrid,
    buildTravelAuditFields,
} from 'components/travel/TravelDetailSection';

const headerActionButtonSx = {
    background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
    color: '#ffffff',
    '&:hover': {
        background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
    },
    transition: 'all 0.2s ease-in-out',
};

const Field = ({
    label,
    value,
    xs = 6,
    isEditing = false,
    readOnly = false,
    options = null,
    isMultiSelect = false,
    onSave,
}) => {
    const isEmpty = value === null || value === undefined || value === '';
    return (
        <Grid item xs={12} md={xs}>
            <InlineEditField
                label={label}
                value={isMultiSelect ? (Array.isArray(value) ? value : []) : (!isEmpty ? value : '')}
                options={options}
                isMultiSelect={isMultiSelect}
                readOnly={readOnly}
                isEditing={isEditing && !readOnly}
                onSave={onSave}
            />
        </Grid>
    );
};

const DetailSection = ({ title, children, mb = 3, headerAction = null }) => (
    <MDBox
        mb={mb}
        p={3}
        borderRadius="lg"
        sx={{
            backgroundColor: '#ffffff',
            boxShadow: 3,
        }}
    >
        {title ? (
            <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                mb={2}
            >
                <MDTypography variant="h6" fontWeight="medium" color="dark">
                    {title}
                </MDTypography>
                {headerAction}
            </MDBox>
        ) : null}
        {children}
    </MDBox>
);

const EmptyState = ({ message, icon = null }) => (
    <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={3}
        px={2}
        textAlign="center"
    >
        {icon}
        <MDTypography variant="body2" color="text" mt={icon ? 1 : 0}>
            {message}
        </MDTypography>
    </MDBox>
);

const TripDetail = () => {
    const { tripId: tripIdParam, id: idParam } = useParams();
    const tripId = tripIdParam || idParam;
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const isCloneSession = searchParams.get('fromClone') === '1';
    const { axiosInstance } = useAuth();
    const { toDropdownOptions } = useCurrencyCodes();
    const { countries: geographyCountries } = useCountries();
    const { languages: languageOptions } = useLanguages();
    const heroInputRef = useRef(null);
    const highlightBlobUrlsRef = useRef([]);
    const discardCloneOnCancelRef = useRef(isCloneSession);

    const revokeHighlightBlobUrls = () => {
        highlightBlobUrlsRef.current.forEach((url) => {
            if (url?.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        });
        highlightBlobUrlsRef.current = [];
    };

    const previewHighlightAttachment = async (attachmentId) => {
        if (!attachmentId) return '';
        try {
            const url = await previewPrivateObject({ axiosInstance, attachmentId });
            if (url?.startsWith('blob:')) {
                highlightBlobUrlsRef.current.push(url);
            }
            return url || '';
        } catch (previewError) {
            console.error('Error previewing highlight attachment:', previewError);
            return '';
        }
    };

    const normalizeMatchKey = (value) => (
        String(value || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '')
    );

    const resolveHighlightImages = async (highlights = []) => {
        if (!Array.isArray(highlights) || highlights.length === 0) return [];

        revokeHighlightBlobUrls();

        let attachments = [];
        try {
            const response = await axiosInstance.get('/attachments/', {
                params: { parent_table: 'trip_highlights', parent_id: tripId },
            });
            attachments = (Array.isArray(response.data) ? response.data : [])
                .filter((attachment) => attachment?.file_type?.startsWith('image/'))
                .sort((a, b) => a.id - b.id);
        } catch (attachmentError) {
            console.error('Error fetching highlight attachments:', attachmentError);
        }

        const previewCache = new Map();
        const previewById = async (attachmentId) => {
            if (!attachmentId) return '';
            if (previewCache.has(attachmentId)) {
                return previewCache.get(attachmentId);
            }
            const previewUrl = await previewHighlightAttachment(attachmentId);
            previewCache.set(attachmentId, previewUrl);
            return previewUrl;
        };

        const usedAttachmentIds = new Set();
        const resolved = [];

        for (let index = 0; index < highlights.length; index += 1) {
            const item = highlights[index];
            let attachmentId = item.image_attachment_id || null;
            let image = '';

            if (attachmentId) {
                image = await previewById(attachmentId);
                if (image) {
                    usedAttachmentIds.add(attachmentId);
                }
                // Keep image_attachment_id even if preview fails so we do not drop the link.
            }

            if (!image) {
                const legacyImage = item.image;
                if (legacyImage && !String(legacyImage).startsWith('blob:')) {
                    image = legacyImage;
                }
            }

            resolved.push({
                item,
                index,
                attachmentId,
                image,
            });
        }

        const unusedAttachments = attachments.filter(
            (attachment) => !usedAttachmentIds.has(attachment.id)
        );

        for (const entry of resolved) {
            if (entry.image) continue;

            const highlightKey = normalizeMatchKey(entry.item.name);
            let matchedAttachment = unusedAttachments.find((attachment) => {
                const fileKey = normalizeMatchKey(attachment.file_name);
                return highlightKey && fileKey && (
                    fileKey.includes(highlightKey) || highlightKey.includes(fileKey)
                );
            });

            if (!matchedAttachment && unusedAttachments.length > 0) {
                [matchedAttachment] = unusedAttachments.splice(0, 1);
            } else if (matchedAttachment) {
                const matchIndex = unusedAttachments.findIndex(
                    (attachment) => attachment.id === matchedAttachment.id
                );
                if (matchIndex >= 0) {
                    unusedAttachments.splice(matchIndex, 1);
                }
            }

            if (matchedAttachment) {
                entry.attachmentId = matchedAttachment.id;
                entry.image = await previewById(matchedAttachment.id);
                if (entry.image) {
                    usedAttachmentIds.add(matchedAttachment.id);
                }
            }

            if (!entry.image) {
                const legacyImage = entry.item.image;
                if (legacyImage && !String(legacyImage).startsWith('blob:')) {
                    entry.image = legacyImage;
                }
            }
        }

        return resolved.map(({ item, index, attachmentId, image }) => ({
            id: item.id || `hl-${index}`,
            name: item.name,
            location: item.location,
            image,
            image_attachment_id: attachmentId,
        }));
    };

    const [trip, setTrip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [heroImage, setHeroImage] = useState('');
    const [heroAttachmentId, setHeroAttachmentId] = useState(null);
    const [pendingHeroFile, setPendingHeroFile] = useState(null);
    const [pendingHeroPreviewUrl, setPendingHeroPreviewUrl] = useState('');
    const [heroRemoved, setHeroRemoved] = useState(false);
    const [isHeroBusy, setIsHeroBusy] = useState(false);
    const [highlightDialogOpen, setHighlightDialogOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(isCloneSession);
    const [updatedFields, setUpdatedFields] = useState({});
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const [isCancelLoading, setIsCancelLoading] = useState(false);
    const [isCloning, setIsCloning] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [takenCountries, setTakenCountries] = useState([]);
    const [websiteSite, setWebsiteSite] = useState(null);
    const [publishedPackageIds, setPublishedPackageIds] = useState(() => new Set());

    useEffect(() => {
        discardCloneOnCancelRef.current = isCloneSession;
        if (isCloneSession) {
            setIsEditing(true);
            setSnackbar({
                open: true,
                message: 'Cloned destination — choose a unique country, then Save. Cancel discards this clone.',
                severity: 'info',
            });
        }
    }, [isCloneSession]);

    const clearCloneQueryParam = () => {
        if (!searchParams.has('fromClone')) return;
        const next = new URLSearchParams(searchParams);
        next.delete('fromClone');
        setSearchParams(next, { replace: true });
        discardCloneOnCancelRef.current = false;
    };

    const currencyOptions = useMemo(
        () => toDropdownOptions({ labelFormat: 'codeName' }).map((opt) => ({
            value: opt.value,
            label: opt.label,
        })),
        [toDropdownOptions]
    );

    const clearPendingHero = () => {
        if (pendingHeroPreviewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(pendingHeroPreviewUrl);
        }
        setPendingHeroFile(null);
        setPendingHeroPreviewUrl('');
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    useEffect(() => {
        fetchTrip();
        fetchHeroImage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tripId]);

    useEffect(() => {
        let cancelled = false;

        const loadTakenCountries = async () => {
            if (!axiosInstance || !tripId) return;
            try {
                const response = await axiosInstance.get('/trips');
                if (cancelled) return;
                setTakenCountries(
                    collectTakenCountries(response.data || [], { excludeTripId: tripId })
                );
            } catch (error) {
                console.error('Error loading existing destination countries:', error);
                if (!cancelled) setTakenCountries([]);
            }
        };

        loadTakenCountries();
        return () => {
            cancelled = true;
        };
    }, [axiosInstance, tripId]);

    useEffect(() => {
        let cancelled = false;

        const loadWebsitePublications = async () => {
            if (!axiosInstance) return;
            try {
                const sitesResponse = await axiosInstance.get('/api/v1/websites/sites');
                const site = (sitesResponse.data || [])[0] || null;
                if (!site) {
                    if (!cancelled) {
                        setWebsiteSite(null);
                        setPublishedPackageIds(new Set());
                    }
                    return;
                }

                // Same pagination as the package detail page, but collected once for all packages
                const limit = 100;
                let offset = 0;
                const published = new Set();
                while (true) {
                    const catalogueResponse = await axiosInstance.get(
                        `/api/v1/websites/sites/${site.id}/catalogue`,
                        { params: { offset, limit, published_only: true } }
                    );
                    const rows = catalogueResponse.data || [];
                    rows.forEach((entry) => {
                        if (entry.is_published) {
                            published.add(Number(entry.trip_package_id));
                        }
                    });
                    if (rows.length < limit) break;
                    offset += limit;
                }

                if (cancelled) return;
                setWebsiteSite(site);
                setPublishedPackageIds(published);
            } catch (websiteError) {
                // A user without Website access can still use the Destination page.
                if (!cancelled) {
                    setWebsiteSite(null);
                    setPublishedPackageIds(new Set());
                }
            }
        };

        loadWebsitePublications();
        return () => {
            cancelled = true;
        };
    }, [axiosInstance, tripId]);

    useEffect(() => () => revokeHighlightBlobUrls(), []);

    const handlePreviewAttachment = async (attachment) => {
        if (!attachment?.id) return '';
        try {
            if (!attachment.bucket_name || !attachment.storage_path) return '';
            return await previewPrivateObject({ axiosInstance, attachmentId: attachment.id });
        } catch (previewError) {
            console.error('Error previewing attachment:', previewError);
            return '';
        }
    };

    const fetchHeroImage = async () => {
        try {
            const response = await axiosInstance.get('/attachments/', {
                params: { parent_table: 'travel_orders', parent_id: tripId },
            });
            const attachments = Array.isArray(response.data) ? response.data : [];
            const imageAttachments = attachments.filter(
                (a) => a?.file_type && String(a.file_type).startsWith('image/')
            );
            if (!imageAttachments.length) {
                setHeroImage('');
                setHeroAttachmentId(null);
                return;
            }
            const latest = [...imageAttachments].sort((a, b) => b.id - a.id)[0];
            setHeroAttachmentId(latest.id);
            setHeroImage((await handlePreviewAttachment(latest)) || '');
        } catch (fetchError) {
            console.error('Error fetching attachments:', fetchError);
            setHeroImage('');
            setHeroAttachmentId(null);
        }
    };

    const fetchTrip = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/trips/${tripId}`);
            const data = res.data;
            if (data?.continent) {
                data.continent = normalizeContinentLabel(data.continent);
            }
            const resolvedHighlights = await resolveHighlightImages(data.key_highlights || []);
            const serializedHighlights = serializeHighlightsForApi(resolvedHighlights);

            const needsHighlightRepair = resolvedHighlights.some((resolved, index) => {
                const original = (data.key_highlights || [])[index];
                if (!resolved?.image_attachment_id) return false;
                return resolved.image_attachment_id !== original?.image_attachment_id
                    || (original?.image && String(original.image).startsWith('blob:'));
            });

            if (needsHighlightRepair) {
                try {
                    await axiosInstance.put(`/trips/${tripId}`, {
                        overview: {
                            description: data.description,
                            continent: data.continent,
                            country: data.primary_country,
                            currency: data.currency,
                            languages: Array.isArray(data.languages) ? data.languages : [],
                            key_highlights: serializedHighlights,
                        },
                    });
                } catch (repairError) {
                    console.error('Failed to repair highlight attachment links:', repairError);
                }
            }

            setTrip({ ...data, key_highlights: resolvedHighlights });
            setError(null);
        } catch (fetchError) {
            setError('Failed to load destination details.');
        } finally {
            setLoading(false);
        }
    };

    const getFieldValue = (field) => (
        Object.prototype.hasOwnProperty.call(updatedFields, field)
            ? updatedFields[field]
            : trip?.[field]
    );

    const scrollToCountryField = () => {
        window.requestAnimationFrame(() => {
            const fieldEl = document.querySelector('[data-trip-field="primary_country"]');
            if (fieldEl) {
                fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });
    };

    const handleFieldUpdate = (field, value) => {
        if (field === 'primary_country' && isCountryTaken(value, takenCountries)) {
            setSnackbar({
                open: true,
                message: COUNTRY_ALREADY_EXISTS_MESSAGE,
                severity: 'error',
            });
            scrollToCountryField();
            return;
        }

        setUpdatedFields((prev) => {
            const next = { ...prev, [field]: value };
            if (field === 'continent') {
                const countries = filterGeographyCountriesForContinent(value, geographyCountries);
                const currentCountry = Object.prototype.hasOwnProperty.call(next, 'primary_country')
                    ? next.primary_country
                    : trip?.primary_country;
                if (currentCountry && !isCountryInContinentList(currentCountry, countries)) {
                    next.primary_country = '';
                }
            }
            return next;
        });
    };

    const getHighlights = () => {
        const value = getFieldValue('key_highlights');
        return Array.isArray(value) ? value : [];
    };

    const updateHighlights = (next) => {
        handleFieldUpdate('key_highlights', next);
    };

    const handleHeroFileSelect = async (file) => {
        if (!file) return;
        const result = await validateTripHeroImage(file);
        if (!result.ok) {
            setSnackbar({
                open: true,
                message: result.message,
                severity: 'error',
            });
            return;
        }
        clearPendingHero();
        setPendingHeroFile(file);
        setPendingHeroPreviewUrl(URL.createObjectURL(file));
        setHeroRemoved(false);
    };

    const handleRemoveHero = () => {
        clearPendingHero();
        setHeroRemoved(true);
    };

    const handleSaveHighlight = (highlight) => {
        const current = getHighlights();
        const exists = current.some((item) => item.id === highlight.id);
        updateHighlights(
            exists
                ? current.map((item) => (item.id === highlight.id ? highlight : item))
                : [...current, highlight]
        );
        setHighlightDialogOpen(false);
    };

    const handleDeleteHighlight = (id) => {
        updateHighlights(getHighlights().filter((item) => item.id !== id));
    };

    const uploadPendingHighlightImages = async (highlights = []) => {
        const updated = [];
        for (const item of highlights) {
            if (!(item?.pendingFile instanceof File)) {
                updated.push({
                    id: item.id,
                    name: item.name,
                    location: item.location,
                    image: item.image,
                    image_attachment_id: item.image_attachment_id || null,
                });
                continue;
            }
            try {
                const attachment = await uploadPendingTripImage({
                    axiosInstance,
                    file: item.pendingFile,
                    parentId: tripId,
                    parentTable: 'trip_highlights',
                    featurePath: 'trips/highlights',
                });
                let imageUrl = '';
                try {
                    imageUrl = await previewHighlightAttachment(attachment.id);
                } catch (previewError) {
                    console.error('Error previewing highlight attachment:', previewError);
                }
                updated.push({
                    id: item.id,
                    name: item.name,
                    location: item.location,
                    image: imageUrl,
                    image_attachment_id: attachment.id,
                });
            } catch (uploadError) {
                console.error('Error uploading highlight image:', uploadError);
                updated.push({
                    id: item.id,
                    name: item.name,
                    location: item.location,
                    image: item.image,
                    image_attachment_id: item.image_attachment_id || null,
                });
            }
        }
        return updated;
    };

    const syncHeroImageChanges = async () => {
        if (heroRemoved && heroAttachmentId) {
            await axiosInstance.delete(`/attachments/${heroAttachmentId}`);
            setHeroAttachmentId(null);
            setHeroImage('');
        }
        if (pendingHeroFile) {
            if (heroAttachmentId && !heroRemoved) {
                await axiosInstance.delete(`/attachments/${heroAttachmentId}`);
            }
            const uploaded = await uploadPendingTripImage({
                axiosInstance,
                file: pendingHeroFile,
                parentId: tripId,
                parentTable: 'travel_orders',
                featurePath: 'trip-banners',
            });
            clearPendingHero();
            setHeroRemoved(false);
            setHeroAttachmentId(uploaded.id);
            const preview = await handlePreviewAttachment(uploaded);
            setHeroImage(preview || uploaded.file_url || '');
        } else if (heroRemoved) {
            setHeroRemoved(false);
        }
    };

    const handleEditToggle = () => {
        setUpdatedFields({});
        clearPendingHero();
        setHeroRemoved(false);
        setIsEditing(true);
    };

    const handleDiscardChanges = async () => {
        setIsCancelLoading(true);
        try {
            if (discardCloneOnCancelRef.current && tripId) {
                try {
                    await axiosInstance.delete(`/trips/${tripId}`);
                    navigate('/trips');
                    return;
                } catch (deleteError) {
                    const errorMessage = deleteError.response?.data?.detail
                        || deleteError.response?.data?.message
                        || deleteError.message
                        || 'Unable to discard cloned destination.';
                    setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                    return;
                }
            }
            setUpdatedFields({});
            clearPendingHero();
            setHeroRemoved(false);
            setHighlightDialogOpen(false);
            setIsEditing(false);
        } finally {
            setIsCancelLoading(false);
        }
    };

    const handleSaveChanges = async () => {
        const hasFieldChanges = Object.keys(updatedFields).length > 0;
        const hasHeroChanges = Boolean(pendingHeroFile || heroRemoved);
        const mustSaveCloneCountry = discardCloneOnCancelRef.current || isCloneSession;

        if (!tripId || (!hasFieldChanges && !hasHeroChanges && !mustSaveCloneCountry)) {
            setIsEditing(false);
            return;
        }

        setIsSaveLoading(true);
        setIsHeroBusy(true);
        try {
            const continent = getFieldValue('continent');
            const country = getFieldValue('primary_country');
            const currency = getFieldValue('currency');
            const languages = getFieldValue('languages');
            const status = getFieldValue('status');
            let highlights = getHighlights();

            if (mustSaveCloneCountry && !String(country || '').trim()) {
                setSnackbar({
                    open: true,
                    message: CLONE_COUNTRY_REQUIRED_MESSAGE,
                    severity: 'error',
                });
                scrollToCountryField();
                return;
            }

            if (isCountryTaken(country, takenCountries)) {
                setSnackbar({
                    open: true,
                    message: COUNTRY_ALREADY_EXISTS_MESSAGE,
                    severity: 'error',
                });
                scrollToCountryField();
                return;
            }

            if (hasHeroChanges) {
                await syncHeroImageChanges();
            }

            if (highlights.some((item) => item?.pendingFile instanceof File)) {
                highlights = await uploadPendingHighlightImages(highlights);
            }

            const serializedHighlights = serializeHighlightsForApi(highlights);

            await axiosInstance.put(`/trips/${tripId}`, {
                overview: {
                    description: getFieldValue('description') || null,
                    continent: continent || null,
                    country: country || null,
                    currency: currency || null,
                    languages: Array.isArray(languages) ? languages : [],
                    key_highlights: serializedHighlights,
                },
                status: status || 'draft',
            });

            setTrip((prev) => ({
                ...prev,
                description: getFieldValue('description') || null,
                continent,
                primary_country: country,
                currency,
                languages: Array.isArray(languages) ? languages : [],
                status: status || 'draft',
                key_highlights: highlights,
            }));
            setUpdatedFields({});
            setIsEditing(false);
            clearCloneQueryParam();
            setSnackbar({
                open: true,
                message: mustSaveCloneCountry
                    ? 'Cloned destination saved successfully.'
                    : 'Destination updated successfully.',
                severity: 'success',
            });
        } catch (saveError) {
            const detail = saveError.response?.data?.detail;
            const isCountryConflict = saveError?.response?.status === 409
                && typeof detail === 'string'
                && /country/i.test(detail);
            const errorMessage = isCountryConflict
                ? COUNTRY_ALREADY_EXISTS_MESSAGE
                : (detail
                    || saveError.response?.data?.message
                    || saveError.message
                    || 'Unable to save destination changes.');
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
            if (isCountryConflict) scrollToCountryField();
        } finally {
            setIsSaveLoading(false);
            setIsHeroBusy(false);
        }
    };

    const handleCloneTrip = async () => {
        if (!tripId || isCloning) return;
        setIsCloning(true);
        try {
            // Deep-clone as draft (country cleared), then open detail page in edit mode
            // — same UI as Edit Destination, not the step wizard.
            const res = await axiosInstance.post(`/trips/${tripId}/clone`);
            const newId = res.data?.id;
            if (!newId) {
                throw new Error(res.data?.message || 'Clone did not return a destination id.');
            }
            navigate(`/trips/${newId}?fromClone=1`);
        } catch (cloneError) {
            const errorMessage = cloneError.response?.data?.detail
                || cloneError.response?.data?.message
                || cloneError.message
                || 'Unable to clone destination.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        } finally {
            setIsCloning(false);
        }
    };

    const handleDeleteTrip = async () => {
        if (!tripId || isDeleting) return;
        setIsDeleting(true);
        try {
            await axiosInstance.delete(`/trips/${tripId}`);
            setSnackbar({
                open: true,
                message: 'Destination deleted successfully.',
                severity: 'success',
            });
            setTimeout(() => navigate('/trips'), 800);
        } catch (deleteError) {
            const errorMessage = deleteError.response?.data?.detail
                || deleteError.response?.data?.message
                || deleteError.message
                || 'Unable to delete destination.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
            throw deleteError;
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <DataLoadingSpinner
                    entityType="default"
                    title="Loading Destination Data"
                    message="Please wait while we fetch the destination information..."
                />
            </DashboardLayout>
        );
    }

    if (error || !trip) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} px={3}>
                    <Card>
                        <MDBox p={4} textAlign="center">
                            <MDTypography color="error" mb={2}>
                                {error || 'Destination not found.'}
                            </MDTypography>
                            <MDButton variant="gradient" color="info" onClick={() => navigate('/trips')}>
                                Back to Travel Destinations
                            </MDButton>
                        </MDBox>
                    </Card>
                </MDBox>
            </DashboardLayout>
        );
    }

    const packages = Array.isArray(trip.packages) ? trip.packages : [];
    const highlights = getHighlights();
    const displayContinent = getFieldValue('continent');
    const displayCountry = getFieldValue('primary_country');
    const filteredCountryOptions = filterGeographyCountriesForContinent(
        displayContinent,
        geographyCountries
    );
    const currentCountryLabel = String(displayCountry || '').trim();
    const countryOptions =
        currentCountryLabel &&
        !filteredCountryOptions.some(
            (name) => name.toLowerCase() === currentCountryLabel.toLowerCase()
        )
            ? [currentCountryLabel, ...filteredCountryOptions]
            : filteredCountryOptions;
    const selectedCountry = matchCountryValue(displayCountry, countryOptions);
    const selectedCountryTaken = isCountryTaken(selectedCountry, takenCountries);
    const countryName = displayCountry || trip.primary_country || 'Untitled Destination';
    const displayHeroUrl = heroRemoved
        ? ''
        : (pendingHeroPreviewUrl || heroImage);
    const displayDescription = getFieldValue('description') || '';
    const hasDescription = getQuillPlainLength(displayDescription) > 0;

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                            >
                                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                    <MDBox>
                                        <MDTypography variant="h6" color="white">
                                            Destination Details: {countryName}
                                        </MDTypography>
                                        <MDBox display="flex" alignItems="center" gap={1} mt={0.75} flexWrap="wrap">
                                            <StatusBadge status={getFieldValue('status') || trip.status || 'draft'} />
                                            {displayContinent && (
                                                <Chip
                                                    label={displayContinent}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 'rgba(255,255,255,0.18)',
                                                        color: '#ffffff',
                                                        border: '1px solid rgba(255,255,255,0.35)',
                                                        height: 24,
                                                        '& .MuiChip-label': {
                                                            color: '#ffffff',
                                                            fontWeight: 500,
                                                        },
                                                    }}
                                                />
                                            )}
                                        </MDBox>
                                    </MDBox>

                                    {!isEditing ? (
                                        <MDBox display="flex" gap={1} alignItems="center">
                                            <Tooltip title="Edit Destination" arrow>
                                                <IconButton onClick={handleEditToggle} sx={commonIconButtonStyles}>
                                                    <EditIcon sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={isCloning ? 'Cloning…' : 'Clone Destination'} arrow>
                                                <span>
                                                    <IconButton
                                                        onClick={handleCloneTrip}
                                                        disabled={isCloning}
                                                        sx={commonIconButtonStyles}
                                                    >
                                                        {isCloning
                                                            ? <CircularProgress size={18} sx={{ color: '#ffffff' }} />
                                                            : <FileCopyIcon sx={commonIconStyles} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Delete Destination" arrow>
                                                <IconButton
                                                    onClick={() => setDeleteConfirmOpen(true)}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <DeleteIcon sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </MDBox>
                                    ) : (
                                        <MDBox display="flex" gap={2}>
                                            <MDButton
                                                variant="contained"
                                                onClick={handleDiscardChanges}
                                                disabled={isCancelLoading || isSaveLoading}
                                                sx={headerActionButtonSx}
                                                startIcon={
                                                    isCancelLoading
                                                        ? <CircularProgress size={18} sx={{ color: '#ffffff' }} />
                                                        : <CloseIcon />
                                                }
                                            >
                                                {isCloneSession ? 'Discard Clone' : 'Cancel'}
                                            </MDButton>
                                            <MDButton
                                                variant="contained"
                                                onClick={handleSaveChanges}
                                                disabled={isCancelLoading || isSaveLoading}
                                                sx={headerActionButtonSx}
                                                startIcon={
                                                    isSaveLoading
                                                        ? <CircularProgress size={18} sx={{ color: '#ffffff' }} />
                                                        : <SaveIcon />
                                                }
                                            >
                                                Save
                                            </MDButton>
                                        </MDBox>
                                    )}
                                </MDBox>
                            </MDBox>

                            <MDBox p={3}>
                                <DetailSection title="Destination Details">
                                    <Grid container spacing={3}>
                                        <Field
                                            label="Continent"
                                            value={displayContinent}
                                            isEditing={isEditing}
                                            options={CONTINENTS}
                                            onSave={(value) => handleFieldUpdate('continent', value)}
                                        />
                                        <Grid item xs={12} md={6} data-trip-field="primary_country">
                                            {isEditing ? (
                                                <Autocomplete
                                                    size="small"
                                                    options={countryOptions}
                                                    value={selectedCountry}
                                                    disabled={!displayContinent}
                                                    getOptionDisabled={(option) => isCountryTaken(option, takenCountries)}
                                                    onChange={(_, newValue) => {
                                                        if (newValue && isCountryTaken(newValue, takenCountries)) {
                                                            return;
                                                        }
                                                        handleFieldUpdate('primary_country', newValue || '');
                                                    }}
                                                    renderOption={(props, option) => {
                                                        const taken = isCountryTaken(option, takenCountries);
                                                        return (
                                                            <li {...props} key={option}>
                                                                {option}{taken ? ' (already exists)' : ''}
                                                            </li>
                                                        );
                                                    }}
                                                    ListboxProps={{
                                                        style: { maxHeight: 280 },
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            {...outlinedFieldProps}
                                                            label="Country"
                                                            placeholder={
                                                                displayContinent
                                                                    ? 'Type to search country'
                                                                    : 'Select continent first'
                                                            }
                                                            error={selectedCountryTaken}
                                                            helperText={
                                                                selectedCountryTaken
                                                                    ? COUNTRY_ALREADY_EXISTS_MESSAGE
                                                                    : ''
                                                            }
                                                        />
                                                    )}
                                                />
                                            ) : (
                                                <InlineEditField
                                                    label="Country"
                                                    value={displayCountry || ''}
                                                    readOnly
                                                    isEditing={false}
                                                />
                                            )}
                                        </Grid>
                                        <Field
                                            label="Currency"
                                            value={getFieldValue('currency')}
                                            isEditing={isEditing}
                                            options={currencyOptions}
                                            onSave={(value) => handleFieldUpdate('currency', value)}
                                        />
                                        <Field
                                            label="Destination Status"
                                            value={getFieldValue('status') || 'draft'}
                                            isEditing={isEditing}
                                            options={[
                                                { value: 'draft', label: 'Draft' },
                                                { value: 'active', label: 'Active' },
                                                { value: 'inactive', label: 'Inactive' },
                                            ]}
                                            onSave={(value) => handleFieldUpdate('status', value)}
                                        />
                                        <Field
                                            label="Languages"
                                            value={
                                                Array.isArray(getFieldValue('languages'))
                                                    ? getFieldValue('languages')
                                                    : []
                                            }
                                            isEditing={isEditing}
                                            options={languageOptions}
                                            isMultiSelect
                                            xs={12}
                                            onSave={(value) => handleFieldUpdate('languages', value)}
                                        />
                                    </Grid>
                                </DetailSection>

                                <DetailSection title="Overview & Media">
                                    {displayHeroUrl ? (
                                        <Box>
                                            <Box
                                                component="img"
                                                src={displayHeroUrl}
                                                alt={countryName}
                                                sx={{
                                                    width: '100%',
                                                    maxHeight: 340,
                                                    objectFit: 'cover',
                                                    borderRadius: 2,
                                                    display: 'block',
                                                }}
                                            />
                                            {isEditing && (
                                                <MDBox display="flex" gap={1} mt={1.5} flexWrap="wrap">
                                                    <MDButton
                                                        variant="outlined"
                                                        color="info"
                                                        size="small"
                                                        disabled={isHeroBusy || isSaveLoading}
                                                        onClick={() => heroInputRef.current?.click()}
                                                    >
                                                        Replace Photo
                                                    </MDButton>
                                                    <MDButton
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        disabled={isHeroBusy || isSaveLoading}
                                                        onClick={handleRemoveHero}
                                                    >
                                                        Remove
                                                    </MDButton>
                                                </MDBox>
                                            )}
                                        </Box>
                                    ) : isEditing ? (
                                        <MDButton
                                            variant="outlined"
                                            color="info"
                                            size="small"
                                            startIcon={<ImageOutlinedIcon />}
                                            disabled={isHeroBusy || isSaveLoading}
                                            onClick={() => heroInputRef.current?.click()}
                                        >
                                            Upload Hero Image
                                        </MDButton>
                                    ) : (
                                        <MDTypography variant="body2" color="text">
                                            No hero image uploaded
                                        </MDTypography>
                                    )}
                                    <input
                                        ref={heroInputRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                            handleHeroFileSelect(e.target.files?.[0]);
                                            e.target.value = '';
                                        }}
                                    />
                                    <MDTypography variant="caption" color="text" display="block" mt={1}>
                                        {tripHeroImageHint}
                                    </MDTypography>

                                    <MDBox mt={3}>
                                        {isEditing ? (
                                            <HtmlFieldWithLimit
                                                label="Description"
                                                value={displayDescription}
                                                onChange={(value) => handleFieldUpdate('description', value)}
                                                maxLen={2000}
                                            />
                                        ) : hasDescription ? (
                                            <Box
                                                sx={{ '& p': { m: 0 }, '& ul, & ol': { pl: 2, my: 0.5 } }}
                                                dangerouslySetInnerHTML={{ __html: displayDescription }}
                                            />
                                        ) : (
                                            <MDTypography variant="body2" color="text">
                                                No description added.
                                            </MDTypography>
                                        )}
                                    </MDBox>
                                </DetailSection>

                                <DetailSection
                                    title={`Key Highlights (${highlights.length})`}
                                    mb={3}
                                    headerAction={
                                        isEditing ? (
                                            <MDButton
                                                variant="outlined"
                                                color="info"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => setHighlightDialogOpen(true)}
                                                sx={{ flexShrink: 0 }}
                                            >
                                                Add Highlight
                                            </MDButton>
                                        ) : null
                                    }
                                >
                                    {highlights.length === 0 ? (
                                        <EmptyState
                                            message={isEditing ? 'No key highlights yet. Click Add Highlight to create one.' : 'No key highlights added.'}
                                            icon={<PlaceOutlinedIcon sx={{ fontSize: 36, color: 'text.secondary' }} />}
                                        />
                                    ) : (
                                        <Grid container spacing={2} mb={isEditing ? 2 : 0}>
                                            {highlights.map((item) => (
                                                <Grid item xs={12} sm={6} md={4} key={item.id || item.name}>
                                                    <Box
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 2,
                                                            overflow: 'hidden',
                                                            height: '100%',
                                                            transition: 'box-shadow 0.2s',
                                                            '&:hover': { boxShadow: 3 },
                                                        }}
                                                    >
                                                        {item.image ? (
                                                            <Box
                                                                component="img"
                                                                src={item.image}
                                                                alt={item.name}
                                                                sx={{
                                                                    width: '100%',
                                                                    height: 140,
                                                                    objectFit: 'cover',
                                                                    display: 'block',
                                                                }}
                                                            />
                                                        ) : (
                                                            <MDBox
                                                                height={140}
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                sx={{ bgcolor: 'grey.100' }}
                                                            >
                                                                <ImageOutlinedIcon sx={{ color: 'text.secondary' }} />
                                                            </MDBox>
                                                        )}
                                                        <MDBox p={1.5}>
                                                            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                                                <MDBox minWidth={0}>
                                                                    <MDTypography
                                                                        variant="subtitle2"
                                                                        fontWeight="medium"
                                                                        sx={{
                                                                            whiteSpace: 'normal',
                                                                            wordBreak: 'break-word',
                                                                            overflowWrap: 'anywhere',
                                                                            lineHeight: 1.35,
                                                                        }}
                                                                    >
                                                                        {item.name || '—'}
                                                                    </MDTypography>
                                                                    <MDTypography
                                                                        variant="caption"
                                                                        color="text"
                                                                        display="block"
                                                                        sx={{
                                                                            whiteSpace: 'normal',
                                                                            wordBreak: 'break-word',
                                                                            overflowWrap: 'anywhere',
                                                                            lineHeight: 1.35,
                                                                        }}
                                                                    >
                                                                        {item.location || '—'}
                                                                    </MDTypography>
                                                                </MDBox>
                                                                {isEditing && (
                                                                    <Tooltip title="Delete highlight">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => handleDeleteHighlight(item.id)}
                                                                        >
                                                                            <DeleteOutlineIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </MDBox>
                                                        </MDBox>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </DetailSection>

                                <DetailSection title="Audit Information" mb={0}>
                                    <TravelDetailFieldGrid
                                        data={trip}
                                        fields={buildTravelAuditFields((userId) =>
                                            navigate(`/users/${userId}/profile`)
                                        )}
                                    />
                                </DetailSection>
                            </MDBox>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={2}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Inventory2OutlinedIcon sx={{ color: 'white !important', mr: 1 }} />
                                    <MDTypography variant="h6" color="white">
                                        Packages ({packages.length})
                                    </MDTypography>
                                </Box>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => navigate(`/trips/${tripId}/edit?step=packages`)}
                                >
                                    Add
                                </MDButton>
                            </MDBox>

                            <MDBox p={2}>
                                {packages.length === 0 ? (
                                    <EmptyState
                                        message="No packages linked to this destination."
                                        icon={<Inventory2OutlinedIcon sx={{ fontSize: 36, color: 'text.secondary' }} />}
                                    />
                                ) : (
                                    packages.map((pkg, index) => (
                                        <Box key={pkg.id}>
                                            {index > 0 && <Divider sx={{ my: 1.5 }} />}
                                            <MDBox
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="flex-start"
                                                gap={1}
                                                sx={{
                                                    cursor: 'pointer',
                                                    borderRadius: 1.5,
                                                    p: 1,
                                                    mx: -1,
                                                    '&:hover': { bgcolor: 'rgba(23, 193, 232, 0.06)' },
                                                }}
                                                onClick={() =>
                                                    navigate(`/trip-packages/detail/${pkg.id}`, {
                                                        state: { name: pkg.title },
                                                    })
                                                }
                                            >
                                                <MDBox minWidth={0} flex={1}>
                                                    <MDTypography
                                                        variant="button"
                                                        fontWeight="medium"
                                                        color="dark"
                                                        display="block"
                                                        sx={{
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'anywhere',
                                                            lineHeight: 1.35,
                                                        }}
                                                    >
                                                        {pkg.title || 'Untitled Package'}
                                                    </MDTypography>
                                                    <MDBox display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                                                        {pkg.duration_days && (
                                                            <MDTypography variant="caption" color="text">
                                                                {pkg.duration_days} days
                                                            </MDTypography>
                                                        )}
                                                        {pkg.package_tier && (
                                                            <Chip
                                                                label={String(pkg.package_tier).replace(/_/g, ' ')}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ height: 20, textTransform: 'capitalize', fontSize: '0.65rem' }}
                                                            />
                                                        )}
                                                    </MDBox>
                                                    {pkg.updated_at && (
                                                        <MonoText sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                                            Updated {new Date(pkg.updated_at).toLocaleDateString()}
                                                        </MonoText>
                                                    )}
                                                </MDBox>
                                                <MDBox display="flex" flexDirection="column" alignItems="flex-end" gap={0.5} flexShrink={0}>
                                                    <StatusBadge status={pkg.status || 'draft'} />
                                                    {websiteSite && (
                                                        <Tooltip
                                                            title={
                                                                publishedPackageIds.has(Number(pkg.id))
                                                                    ? `Published to ${websiteSite.name}`
                                                                    : `Not published to ${websiteSite.name}`
                                                            }
                                                            arrow
                                                        >
                                                            <Chip
                                                                label={publishedPackageIds.has(Number(pkg.id)) ? 'Published' : 'Not Published'}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: '0.65rem',
                                                                    fontWeight: 600,
                                                                    ...(publishedPackageIds.has(Number(pkg.id))
                                                                        ? { color: 'success.main', borderColor: 'success.main', bgcolor: 'rgba(76, 175, 80, 0.08)' }
                                                                        : { color: 'text.secondary', borderColor: 'grey.400', bgcolor: 'grey.100' }),
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </MDBox>
                                            </MDBox>
                                        </Box>
                                    ))
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <AddHighlightDialog
                open={highlightDialogOpen}
                onClose={() => setHighlightDialogOpen(false)}
                onSave={handleSaveHighlight}
            />

            <ConfirmDeleteModal
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDeleteTrip}
                title="Delete Destination"
                message={(
                    <>
                        <MDTypography variant="body1" sx={{ color: '#34495e', fontSize: '16px', lineHeight: 1.6, mb: packages.length ? 1.5 : 0 }}>
                            {`Are you sure you want to delete "${countryName}"? This action cannot be undone.`}
                        </MDTypography>
                        {packages.length > 0 ? (
                            <>
                                <MDTypography variant="body2" fontWeight="medium" sx={{ color: '#34495e', mb: 1 }}>
                                    {`This will also remove ${packages.length} associated package${packages.length === 1 ? '' : 's'}:`}
                                </MDTypography>
                                <Box
                                    component="ul"
                                    sx={{
                                        m: 0,
                                        pl: 2.5,
                                        maxHeight: 180,
                                        overflowY: 'auto',
                                    }}
                                >
                                    {packages.map((pkg) => (
                                        <Box component="li" key={pkg.id} sx={{ mb: 0.5 }}>
                                            <MDTypography variant="body2" sx={{ color: '#34495e' }}>
                                                {pkg.title || 'Untitled Package'}
                                            </MDTypography>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        ) : (
                            <MDTypography variant="body2" sx={{ color: '#34495e', mt: 1 }}>
                                There are no associated packages for this destination.
                            </MDTypography>
                        )}
                    </>
                )}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TripDetail;
